// KPICodeItem.jsx
import React from "react";
import { Input, NumberInput } from '../../../../../common/Input';
import { Button, ButtonIcon } from "../../../../../common/APICommon";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

export function KPICodeItem({
  index,
  code,
  isEditing,
  errorState,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onRemoveCode,
  onCodeChange
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-100 transition-colors">
      {/* Header with Actions */}
      <div className="flex items-center justify-between mb-3 hidden">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
          <h4 className="font-medium text-gray-700">
            Code #{index + 1}
          </h4>
          {isEditing && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
              Editing
            </span>
          )}
        </div>
        <div className="flex items-center space-x-1">
          {!isEditing ? (
            <>
              <Button
                icon={<EditIcon fontSize="small" />}
                name={"Edit"}
                className={"bg-blue-500 hover:bg-blue-600 border-none cursor-pointer text-sm px-3 py-1.5"}
                onClick={() => onStartEdit(index)}
              />
              <Button
                icon={<DeleteIcon fontSize="small" />}
                name={"Remove"}
                className={"bg-red-500 hover:bg-red-600 border-none cursor-pointer text-sm px-3 py-1.5"}
                onClick={() => onRemoveCode(index)}
              />
            </>
          ) : (
            <>
              <Button
                icon={<SaveIcon fontSize="small" />}
                name={"Save"}
                className={"bg-green-500 hover:bg-green-600 border-none cursor-pointer text-sm px-3 py-1.5"}
                onClick={onSaveEdit}
              />
              <Button
                icon={<CancelIcon fontSize="small" />}
                name={"Cancel"}
                className={"bg-gray-400 hover:bg-gray-500 border-none cursor-pointer text-sm px-3 py-1.5"}
                onClick={onCancelEdit}
              />
            </>
          )}
        </div>
      </div>

      {/* Code Fields */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Code Field */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
            Code
          </label>
          {isEditing ? (
            <Input
              value={code.code || ""}
              onChange={(e) => onCodeChange(index, 'code', e.target.value)}
              placeholder="e.g., A, B, C"
              isRequired={true}
              error={errorState[0]?.code && !code.code}
              errorMessage={errorState[0]?.code}
              className="text-sm"
              inputClassName="py-1.5 text-sm"
            />
          ) : (
            <div className="flex items-center">
              <div className="bg-gray-100 text-gray-800 px-3 py-1.5 rounded text-sm font-medium">
                {code.code || "Not set"}
              </div>
            </div>
          )}
        </div>

        {/* Rating Field */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
            Rating
          </label>
          {isEditing ? (
            <NumberInput
              value={code.rating || ""}
              onChange={(e) => onCodeChange(index, 'rating', e.target.value)}
              placeholder="e.g., 1, 2, 3"
              isRequired={true}
              min="1"
              step="1"
              error={errorState[0]?.rating && !code.rating}
              errorMessage={errorState[0]?.rating}
              className="text-sm"
              inputClassName="py-1.5 text-sm"
            />
          ) : (
            <div className="flex items-center">
              <div className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded text-sm font-medium">
                {code.rating || "Not set"}
              </div>
            </div>
          )}
        </div>

        {/* Description Field */}
        <div className="md:col-span-1">
          <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
            Description
          </label>
          {isEditing ? (
            <Input
              type="textarea"
              value={code.description || ""}
              onChange={(e) => onCodeChange(index, 'description', e.target.value)}
              placeholder="Enter description"
              isRequired={true}
              rows={2}
              error={errorState[0]?.description && !code.description}
              errorMessage={errorState[0]?.description}
              className="text-sm"
              textareaClassName="py-1.5 text-sm"
            />
          ) : (
            <div className="flex items-center">
              <div className="text-blue-gray px-3 py-1.5 rounded text-sm font-medium">
                {code.description || "Not set"}
              </div>
            </div>
          )}
        </div>

        {/* Edit Delete Cancel Button */}
        <div className="flex items-end space-x-1">
          {!isEditing ? (
            <>
              <ButtonIcon
                icon={<EditIcon fontSize="small" />}
                className={"bg-blue-500 hover:bg-blue-600 border-none cursor-pointer text-sm px-3 py-1.5"}
                onClick={() => onStartEdit(index)}
              />
              <ButtonIcon
                icon={<DeleteIcon fontSize="small" />}
                className={"bg-red-500 hover:bg-red-600 border-none cursor-pointer text-sm px-3 py-1.5"}
                onClick={() => onRemoveCode(index)}
              />
            </>
          ) : (
            <div className="flex mb-1.5">
              <ButtonIcon
                icon={<SaveIcon fontSize="small" />}
                className={"bg-green-500 hover:bg-green-600 border-none cursor-pointer text-sm px-3 py-1.5"}
                onClick={onSaveEdit}
              />
              <ButtonIcon
                icon={<CancelIcon fontSize="small" />}
                className={"bg-gray-400 hover:bg-gray-500 border-none cursor-pointer text-sm px-3 py-1.5 ml-1"}
                onClick={onCancelEdit}
              />
            </div>
          )}
        </div>
      </div>

      {/* Error Summary for Edit Mode */}
      {isEditing && (errorState[0]?.code || errorState[0]?.description || errorState[0]?.rating) && (
        <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded text-xs">
          <div className="flex items-start">
            <svg className="w-4 h-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-medium text-red-700 mb-1">Please fix the following issues:</p>
              <ul className="text-red-600 list-disc list-inside space-y-0.5">
                {errorState[0]?.code && <li className="text-xs">{errorState[0]?.code}</li>}
                {errorState[0]?.description && <li className="text-xs">{errorState[0]?.description}</li>}
                {errorState[0]?.rating && <li className="text-xs">{errorState[0]?.rating}</li>}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}